﻿
/**
 * 
 * Viwo Accordion
 * 
 **/

const DEFAULTS = {
    item: "[data-viwo-accordion-item]",
    trigger: "[data-viwo-accordion-trigger]",
    body: "[data-viwo-accordion-body]",
    openClass: "is-open",
    initializedClass: "is-initialized"
};

export function initViwoAccordions(root = document) {
    const accordions = Array.from(root.querySelectorAll("[data-viwo-accordion]"));

    accordions.forEach(accordion => {
        if (accordion.dataset.viwoAccordionInitialized === "true") return;

        accordion.dataset.viwoAccordionInitialized = "true";
        setupAccordion(accordion);
    });
}

function setupAccordion(accordion) {
    const items = getDirectItems(accordion);
    const allowMultiple = getBool(accordion.dataset.viwoAccordionMultiple, false);
    const closeOnOpen = !allowMultiple;
    const collapsible = getBool(accordion.dataset.viwoAccordionCollapsible, true);

    items.forEach((item, index) => {
        const trigger = getTrigger(item);
        const body = getBody(item);

        if (!trigger || !body) return;

        const isInitiallyOpen =
            item.classList.contains(DEFAULTS.openClass) ||
            item.hasAttribute("data-open") ||
            trigger.getAttribute("aria-expanded") === "true";

        prepareItem(item, trigger, body, isInitiallyOpen, index);

        trigger.addEventListener("click", () => {
            const isOpen = item.classList.contains(DEFAULTS.openClass);

            if (isOpen) {
                if (collapsible) {
                    closeItem(item);
                }

                return;
            }

            if (closeOnOpen) {
                items.forEach(other => {
                    if (other !== item) closeItem(other);
                });
            }

            openItem(item);
        });

        trigger.addEventListener("keydown", (event) => {
            handleKeyboard(event, items, item);
        });
    });
}

function prepareItem(item, trigger, body, isOpen, index) {
    item.classList.add(DEFAULTS.initializedClass);

    if (!trigger.id) {
        trigger.id = `viwo-accordion-trigger-${cryptoId()}`;
    }

    if (!body.id) {
        body.id = `viwo-accordion-body-${cryptoId()}`;
    }

    trigger.setAttribute("aria-controls", body.id);
    body.setAttribute("aria-labelledby", trigger.id);
    trigger.setAttribute("type", trigger.getAttribute("type") || "button");

    item.dataset.viwoAccordionIndex = String(index);

    if (isOpen) {
        openItem(item);
    } else {
        closeItem(item);
    }
}

function dispatchAccordionOpened(item) {
    window.dispatchEvent(new CustomEvent("viwo:accordion-opened", {
        detail: {
            item
        }
    }));
}

function openItem(item) {
    const trigger = getTrigger(item);
    const body = getBody(item);

    item.classList.add(DEFAULTS.openClass);

    if (trigger) {
        trigger.setAttribute("aria-expanded", "true");
    }

    if (body) {
        body.setAttribute("aria-hidden", "false");
    }

    window.dispatchEvent(new CustomEvent("viwo:accordion-opened", {
        detail: { item }
    }));
}

function closeItem(item) {
    const trigger = getTrigger(item);
    const body = getBody(item);

    item.classList.remove(DEFAULTS.openClass);

    if (trigger) {
        trigger.setAttribute("aria-expanded", "false");
    }

    if (body) {
        body.setAttribute("aria-hidden", "true");
    }
}

function handleKeyboard(event, items, currentItem) {
    const currentIndex = items.indexOf(currentItem);
    if (currentIndex < 0) return;

    let nextIndex = null;

    if (event.key === "ArrowDown") {
        nextIndex = Math.min(currentIndex + 1, items.length - 1);
    }

    if (event.key === "ArrowUp") {
        nextIndex = Math.max(currentIndex - 1, 0);
    }

    if (event.key === "Home") {
        nextIndex = 0;
    }

    if (event.key === "End") {
        nextIndex = items.length - 1;
    }

    if (nextIndex === null) return;

    event.preventDefault();

    const nextTrigger = getTrigger(items[nextIndex]);
    nextTrigger?.focus();
}

function getDirectItems(accordion) {
    return Array.from(accordion.querySelectorAll(DEFAULTS.item))
        .filter(item => item.closest("[data-viwo-accordion]") === accordion);
}

function getTrigger(item) {
    return item.querySelector(DEFAULTS.trigger);
}

function getBody(item) {
    return item.querySelector(DEFAULTS.body);
}

function getBool(value, fallback) {
    if (value === undefined) return fallback;
    return value === "true" || value === "";
}

function cryptoId() {
    if (window.crypto?.randomUUID) {
        return window.crypto.randomUUID();
    }

    return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}